import React from "react";
import { Field, Form, Formik } from 'formik';
import { Link } from "react-router-dom";

import { ROUTESLIST } from "@/static/urls";
import FormField from "@/components/forms/form-field";
import OneAutocompleteField from "@/components/forms/one-autocomplete";
import { FORMS } from "@/static/common.js";
import GoogleMapsAutocompleteField from "@/components/forms/google-maps-autocomplete";
import * as yup from "yup";
import {arrangeData} from "@/middlewares/api";
import { withFormikFieldAdapter as formikFieldAdapter } from '@/components/forms/formik-adapter';
import { autocompleteString } from '@/utils/forms';

const validationSchema = yup.object({
    name: yup.string().required('Contact name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    label_id: yup.string().transform((v) => {
        if (v == null || v === '') return '';
        if (typeof v === 'object' && v !== null && (v.name != null || v.id != null)) return v.name || String(v.id);
        return String(v);
    }),
    distribution_id: autocompleteString(yup, 'Company distributing your project is required'),
    business_address: yup.string().required('Business address is required'),
    phone_number: yup.string().required('Phone number is required'),
    party_responsible_payment: yup.string().required('Party responsible for payment is required'),
    author_sign_sample_agreement: yup.string().required('Who will sign Sample Agreement is required'),
    party_payment_email: yup.string().email('Invalid email').required('Payment email is required'),
});

const initialValuesShape = {
    name: '',
    email: '',
    label_id: '',
    distribution_id: '',
    business_address: '',
    phone_number: '',
    party_responsible_payment: '',
    author_sign_sample_agreement: '',
    party_payment_email: '',
};

// Stable component references so fields don't remount on every keystroke (which caused blur)
const FormFieldFormik = formikFieldAdapter(FormField);
const OneAutocompleteFieldFormik = formikFieldAdapter(OneAutocompleteField);
const GoogleMapsAutocompleteFieldFormik = formikFieldAdapter(GoogleMapsAutocompleteField);

const SampleStep1 = ({ nextStep, submitting, bgImg, initialValues = {} }) => {
    const values = {
        ...initialValuesShape,
        ...initialValues,
    };
    return (
        <section className="upload-form__sec" style={bgImg}>
            <div className="content">
                <div className="upload-form__top">
                    <h2 className="head-shadow">CLEAR SAMPLES</h2>
                    <h3 className="head">Step 1 of 4: CLIENT INFO</h3>
                </div>
                <div className="form-step__body">
                    <Formik
                        initialValues={values}
                        validationSchema={validationSchema}
                        enableReinitialize
                        onSubmit={(values) => {
                            const payload = arrangeData(values, ['distribution', 'label']);
                            if (nextStep) nextStep(payload, payload);
                            else console.log('nextStep', payload);
                        }}
                    >
                        {() => (
                            <Form className="form-layout" noValidate>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="name"
                                            component={FormFieldFormik}
                                            required={true}
                                            type="text"
                                            placeholder="Set First and Last Name"
                                            label="Contact Name"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="email"
                                            component={FormFieldFormik}
                                            type="email"
                                            required={true}
                                            placeholder="some@email.com"
                                            label="Your email address"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="label_id"
                                            component={OneAutocompleteFieldFormik}
                                            required={false}
                                            type="text"
                                            url="clear/label/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                            label="Name of your Label"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="distribution_id"
                                            component={OneAutocompleteFieldFormik}
                                            type="text"
                                            required={true}
                                            url="clear/distributor/search"
                                            placeholder={FORMS.PLACEHOLDER_TBD}
                                            label="Company distributing your project"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="business_address"
                                            component={GoogleMapsAutocompleteFieldFormik}
                                            type="text"
                                            required={true}
                                            placeholder="Your Business Address"
                                            label="Business Address"
                                            note="Example: 555 Main Street, Anytown, NY, 10019"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="phone_number"
                                            component={FormFieldFormik}
                                            type="text"
                                            maxLength={10}
                                            required={true}
                                            placeholder="5555555555"
                                            label="Your Phone Number"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-col_2">
                                        <Field
                                            name="party_responsible_payment"
                                            component={FormFieldFormik}
                                            type="text"
                                            required={true}
                                            placeholder="Party Responsible For Making Payment?"
                                            label="Party Responsible for Making Payment"
                                        />
                                    </div>
                                    <div className="form-col_2">
                                        <Field
                                            name="author_sign_sample_agreement"
                                            component={FormFieldFormik}
                                            type="text"
                                            required={true}
                                            placeholder="Set First and Last Name"
                                            label="Who will sign Sample Agreement"
                                        />
                                    </div>
                                </div>
                                <Field
                                    name="party_payment_email"
                                    component={FormFieldFormik}
                                    type="email"
                                    required={true}
                                    placeholder="some@email.com"
                                    label="Party Responsible for Making Payment Email Address"
                                />
                                <div className="submit-row_txt">
                                    <p className="as-text">
                                        By clicking Next button you are agreeing to the
                                        <Link
                                            to={ROUTESLIST.STATIC_TERMS}
                                            className="link-thin"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Terms and Conditions.
                                        </Link>
                                    </p>
                                    <div className="submit-row__btn-col">
                                        <button
                                            className="btn-red-primary"
                                            disabled={submitting}
                                            type="submit"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </section>
    );
};

export default SampleStep1;
